import {iEmailAutomation} from "../../types/internal";
import {iApiBasicResponse, iBasicListingResponse, iBasicResourceCreationParams, iListingQuery} from "../../types/api";
import ResourceAPIs, {ObjectFlatter} from "../resource.apis";
import axios, {CancelTokenSource} from "axios";
import {iEmailCampaignRecipientsResponse} from "./email.campaigns.apis";


export interface iEmailFlowsSummary {
    active_flows: number;
    active_flows_change_last_24?: number;
    /** Creation timeline of currently active flows */
    active_flows_series?: number[];
    total_contacts: number;
    total_revenue: number;
    total_revenue_change_last_24?: number;
    /** Last 14+ daily revenue totals (oldest → newest; may expand when recent window is empty) */
    total_revenue_series?: number[];
    /** Successful flow email sends only (tracking_logs with auto_trigger_id) */
    emails_sent?: number;
    emails_sent_change_last_24?: number;
    /** Daily send counts (oldest → newest; expands lookback when needed) */
    emails_sent_series?: number[];
    /** ISO datetime of latest successful flow email send (tracking_logs) */
    last_email_sent_at?: string | null;
}

/** Extensible settings bag on automation listing (active count today; more keys later). */
export interface iEmailAutomationSettings {
    active_automation_count: number;
}

export interface iEmailAutomationsListingResponse extends iApiBasicResponse {
    automations?: iBasicListingResponse<iEmailAutomation>
    flows_summary?: iEmailFlowsSummary
    automation_settings?: iEmailAutomationSettings
}

export interface iEmailAutomationViewResponse extends iApiBasicResponse {
    automation?: iEmailAutomation
}

export interface iDiscountCoupon {
    code?: string,
    discount?: string,
    label?: string
    id?: string
}


export interface iDiscountCouponResponse extends iApiBasicResponse {
    default_discounts?: {
        abandoned_cart_10: iDiscountCoupon
        abandoned_cart_20: iDiscountCoupon
        apology: iDiscountCoupon
        popup_discount_20: iDiscountCoupon
        review_with_no_image_10: iDiscountCoupon
        review_with_image_20: iDiscountCoupon
    }
}

export interface iExplorePublicAutomations extends iApiBasicResponse {
    public_automations:iBasicListingResponse<iEmailAutomation>
}

export interface iAccountLanguage {
    id: number;
    uid: string;
    code: string;
    name: string;
}

export interface iAccountLanguagesResponse extends iApiBasicResponse {
    languages?: iAccountLanguage[];
}

export interface iEmailAutomationCreateParams extends iBasicResourceCreationParams {
    name: string
    mail_list_uid: string
}

export interface AutomationCreationResponse extends iApiBasicResponse {
    url?: string,
    uid?: string,
    status?: string,
}

export interface iAutomationCopyStartResponse extends iApiBasicResponse {
    status?: 'queued' | 'done';
    job_uid?: string;
    message?: string;
    new_automation_uid?: string;
}

export interface iAutomationCopyStatusResponse extends iApiBasicResponse {
    status?: string;
    job_uid?: string;
    last_error?: string;
    data?: {
        status?: string;
        message?: string;
        automation_source_uid?: string;
        new_automation_uid?: string;
    };
}

export default class EmailAutomationAPIs extends ResourceAPIs<iEmailAutomationCreateParams,
    iEmailAutomationsListingResponse, iEmailAutomationViewResponse> {
    getResourceIdentifier = () => "/automation2";
    getResourceCreationURL = () => this.getResourceURL() + "/create";
    getExternalEditorURL = (uid: string) => this.getResourceURL() + `/${uid}/edit`;

    create = async (creationParams: any): Promise<AutomationCreationResponse> => {
        let csrfToken: string = await this.getCsrfToken();
        if (!csrfToken) return {statusCode: 999, errors: {network: "Network error!"}};

        let fd = new FormData();
        fd.set("_token", csrfToken);
        let flattenedParams = new ObjectFlatter(creationParams);
        flattenedParams.get_form_data().forEach(entry => {
            fd.set(entry.key, entry.value);
        });
        return axios
            .post(this.getResourceCreationURL(), fd, {
                headers: {"Content-Type": "application/x-www-form-urlencoded"},
                withCredentials: true,
            })
            .then((res): iApiBasicResponse => ({statusCode: res.status, ...res.data}))
            .catch((error): any => {
                return this.handleCatch(error);
            })
    };
    update_name = async (uid: string, values: any): Promise<AutomationCreationResponse> => {
        let csrfToken: string = await this.getCsrfToken();
        if (!csrfToken) return {statusCode: 999, errors: {network: "Network error!"}};

        let fd = new FormData();
        fd.set("_token", csrfToken);
        let flattenedParams = new ObjectFlatter(values);
        flattenedParams.get_form_data().forEach(entry => {
            fd.set(entry.key, entry.value);
        });
        return axios
            .post(this.getApiBaseURL() + "/automation2/" + uid + "/update", fd, {
                headers: {"Content-Type": "application/x-www-form-urlencoded"},
                withCredentials: true,
            })
            .then((res): iApiBasicResponse => ({statusCode: res.status, ...res.data}))
            .catch((error): any => {
                return this.handleCatch(error);
            })
    };
    load_account_languages = async (): Promise<iAccountLanguagesResponse> => {
        return axios
            .get(this.getApiBaseURL() + "/account/languages", {
                withCredentials: true,
                params: { ts: new Date().getTime() },
            })
            .then((res): iAccountLanguagesResponse => ({
                statusCode: res.status,
                ...res.data,
            }))
            .catch((error): any => this.handleCatch(error));
    };

    copy_automation = async (
        automation_id: string,
        copy_name: string,
        target_language?: string
    ): Promise<iAutomationCopyStartResponse> => {
        let csrfToken: string = await this.getCsrfToken();
        if (!csrfToken) return {statusCode: 999, errors: {network: "Network error!"}};

        let fd = new FormData();
        fd.set("_token", csrfToken);
        fd.set("copy_automation_name", copy_name);
        if (target_language) {
            fd.set("target_language", target_language);
        }
        return axios
            .post(this.getResourceURL() + `/${automation_id}/copy`, fd, {
                withCredentials: true,
                headers: { Accept: "application/json" },
            })
            .then((res): iAutomationCopyStartResponse => {
                const body = res.data;
                if (typeof body === "string") {
                    return {
                        statusCode: res.status,
                        status: body === "Copied" ? "done" : "queued",
                    };
                }
                return { statusCode: res.status, ...body };
            })
            .catch((error): any => {
                return this.handleCatch(error);
            })
    };

    get_copy_status = async (job_uid: string): Promise<iAutomationCopyStatusResponse> => {
        return axios
            .get(this.getApiBaseURL() + `/automation2/copy/status/${job_uid}`, {
                withCredentials: true,
                headers: { Accept: "application/json" },
                params: { ts: new Date().getTime() },
            })
            .then((res): iAutomationCopyStatusResponse => ({
                statusCode: res.status,
                ...res.data,
            }))
            .catch((error): any => {
                return this.handleCatch(error);
            });
    };

    delete = async (uids: Array<string>): Promise<iApiBasicResponse> => {
        let csrfToken: string = await this.getCsrfToken();
        if (!csrfToken) return {statusCode: 999, errors: {network: "Network error!"}};

        let fd = new FormData();
        fd.set("_token", csrfToken);
        return axios
            .delete<iApiBasicResponse>(this.getResourceDeletionURL(), {
                withCredentials: true,
                params: {
                    uids: uids.join(',')
                },
                data: {
                    _token: csrfToken
                }
            })
            // @ts-ignore
            .then((res): iApiBasicResponse => ({statusCode: res.status, ...res.data}))
            .catch((error): any => {
                return this.handleCatch(error);
            })
    };

    enable_disable = async (uids: Array<string>, is_enable: boolean): Promise<iApiBasicResponse> => {
        let csrfToken: string = await this.getCsrfToken();
        if (!csrfToken) return {statusCode: 999, errors: {network: "Network error!"}};


        return axios
            .post(is_enable ? this.getResourceEnableURL() : this.getResourceDisableURL(),
                {_token: csrfToken, "_method": "PATCH"},
                {
                    withCredentials: true,
                    params: {
                        uids: uids.join(',')
                    }
                })
            // @ts-ignore
            .then((res): iApiBasicResponse => ({statusCode: res.status, ...res.data}))
            .catch((error): any => {
                return this.handleCatch(error);
            })
    };
    load_default_discount_coupons = async (): Promise<iDiscountCouponResponse> => {
        return axios
            .get(this.getApiBaseURL() + "/default_automations/discount_coupons",
                {
                    withCredentials: true,
                    params: {
                        ts: new Date().getTime()
                    }
                })
            // @ts-ignore
            .then((res): iDiscountCouponResponse => ({statusCode: res.status, ...res.data}))
            .catch((error): any => {
                return this.handleCatch(error);
            })
    };
    load_public_automations= async (listingQuery: iListingQuery, cancel_token?: CancelTokenSource): Promise<iExplorePublicAutomations> => {
        return axios
            .get(this.getApiBaseURL() + "/automation2/public_automations",
                {
                    withCredentials: true,
                    params: {
                        ...listingQuery,
                        ts: new Date().getTime()
                    },
                    cancelToken: cancel_token && cancel_token.token,
                })
            // @ts-ignore
            .then((res): iExplorePublicAutomations => ({statusCode: res.status, ...res.data}))
            .catch((error): any => {
                return this.handleCatch(error);
            })
    };
    skip_automations = async (): Promise<iDiscountCouponResponse> => {
        let csrfToken: string = await this.getCsrfToken();
        if (!csrfToken) return {statusCode: 999, errors: {network: "Network error!"}};

        return axios
            .post(this.getApiBaseURL() + "/default_automations/skip_automations",
                {_token: csrfToken},
                {
                    withCredentials: true,
                })
            // @ts-ignore
            .then((res): iDiscountCouponResponse => ({statusCode: res.status, ...res.data}))
            .catch((error): any => {
                return this.handleCatch(error);
            })
    };
    automations_imported = async (): Promise<any> => {
        let csrfToken: string = await this.getCsrfToken();
        if (!csrfToken) return {statusCode: 999, errors: {network: "Network error!"}};

        return axios
            .post(this.getApiBaseURL() + `/automations_imported`,
                {_token: csrfToken},
                {
                    withCredentials: true,
                })
            // @ts-ignore
            .then((res): any => ({statusCode: res.status, ...res.data}))
            .catch((error): any => {
                return this.handleCatch(error);
            })
    };
    mark_available_to_public = async (uid:string,data:any): Promise<any> => {
        let csrfToken: string = await this.getCsrfToken();
        if (!csrfToken) return {statusCode: 999, errors: {network: "Network error!"}};

        return axios
            .post(this.getApiBaseURL() + `/automation2/${uid}/update_public_flag`,
                {_token: csrfToken,...data},
                {
                    withCredentials: true,
                })
            // @ts-ignore
            .then((res): any => ({statusCode: res.status, ...res.data}))
            .catch((error): any => {
                return this.handleCatch(error);
            })
    };
    default_automations_create_automations = async (values: any): Promise<iDiscountCouponResponse> => {
        let csrfToken: string = await this.getCsrfToken();
        if (!csrfToken) return {statusCode: 999, errors: {network: "Network error!"}};

        return axios
            .post(this.getApiBaseURL() + "/default_automations/create_automations",
                {_token: csrfToken, ...values},
                {
                    withCredentials: true,
                }
            )
            // @ts-ignore
            .then((res): iDiscountCouponResponse => ({statusCode: res.status, ...res.data}))
            .catch((error): any => {
                return this.handleCatch(error);
            })
    };
    default_discount_coupons = async (values: any): Promise<iApiBasicResponse> => {
        let csrfToken: string = await this.getCsrfToken();
        if (!csrfToken) return {statusCode: 999, errors: {network: "Network error!"}};


        return axios
            .post(this.getResourceIdentifier() + "/default_discount_coupons",
                {_token: csrfToken, ...values},

                {
                    withCredentials: true,
                }
            )
            // @ts-ignore
            .then((res): iApiBasicResponse => ({statusCode: res.status, ...res.data}))
            .catch((error): any => {
                return this.handleCatch(error);
            })
    };
}
